import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Building,
  Building2,
  ChevronRight,
  Download,
  ExternalLink,
  Folder,
  FolderInput,
  FolderPlus,
  Globe,
  HardDrive,
  Mail,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Search,
  Share2,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, requestWithMessage, uploadToDrive, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { formatBytes, formatDateTime, relativeTime } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { DRIVE_KEY, drivePage, type DriveDetails, type DriveDownload, type DriveItem, type DriveOwnerSummary } from '../lib/drive';
import { iconFor, useFilePolicy } from '../components/Attachments';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  ProgressBar,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

/**
 * Viganium Drive (operator, 2026-11-02: "i want to add feature call it Viganium
 * Drive can make folders upload files share it with employees all employees can
 * see each other i can share with public link to let anyone downloaded i can
 * share with clients contacts email folder or file can be related to client or
 * project optional"). Everyone's own drive — My Drive, Shared with me, the Bin —
 * each folder's page, each file's page, and the Drive tab of a client and of a
 * project. The forms (new folder, rename, move, share, email to contacts) are
 * pages of their own, in DriveForms.tsx.
 */

type ListView = 'mine' | 'shared' | 'trash';
type DriveView = ListView | 'everyone';

const VIEWS: Array<{ id: DriveView; to: string; label: TranslationKey; permission?: string }> = [
  { id: 'mine', to: '/drive', label: 'drive.myDrive' },
  { id: 'shared', to: '/drive/shared', label: 'drive.shared' },
  { id: 'trash', to: '/drive/trash', label: 'drive.trash' },
  // Every employee's drive, opened and shared — the administrators' (2026-11-02).
  { id: 'everyone', to: '/drive/everyone', label: 'drive.everyoneTab', permission: P.DRIVE_VIEW_ALL },
];

// ---------------------------------------------------------------------------
// The lists
// ---------------------------------------------------------------------------

/** My Drive, Shared with me and the Bin — and, with `?q=`, a search of everything one may open. */
export function DrivePage({ view }: { view: ListView }) {
  const { t, locale } = useI18n();
  const [params] = useSearchParams();
  const q = view === 'mine' ? (params.get('q') ?? '').trim() : '';
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const actions = useDriveActions();

  const list = useQuery({
    queryKey: q ? [...DRIVE_KEY, 'search', { q }] : [...DRIVE_KEY, view],
    queryFn: () =>
      q
        ? api.get<{ items: DriveItem[] }>('/drive/search', { q })
        : api.get<{ items: DriveItem[]; usage?: { files: number; bytes: number } }>(
            view === 'mine' ? '/drive/root' : `/drive/${view}`,
          ),
  });
  const uploads = useDriveUploads(view === 'mine' && !q, null, t('drive.dropTop'));
  const usage = (list.data as { usage?: { files: number; bytes: number } } | undefined)?.usage;

  return (
    <DropArea uploads={uploads}>
      <PageHeader
        title={t('drive.title')}
        subtitle={
          usage && !q ? t('drive.usage', { files: usage.files, size: formatBytes(usage.bytes, locale) }) : undefined
        }
        action={
          view === 'mine' ? (
            <NewButtons parentId={null} onUpload={uploads.pick} />
          ) : view === 'trash' && list.data?.items.length ? (
            <Button variant="danger" icon={<Trash2 className="h-4 w-4" aria-hidden />} onClick={() => setConfirmEmpty(true)}>
              {t('drive.emptyTrashAction')}
            </Button>
          ) : undefined
        }
      />
      <DriveTabs active={view} />
      <SearchBox initial={q} />

      {q && (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-body-sm text-muted">
          <span>{t('drive.searchResults', { q })}</span>
          <Link to="/drive" className="text-primary-600 hover:underline dark:text-primary-400">
            {t('drive.clearSearch')}
          </Link>
        </div>
      )}

      <Card padded={false} className="mt-4">
        {list.isLoading ? (
          <LoadingBlock />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState
            icon={<HardDrive className="h-8 w-8" aria-hidden />}
            title={t(q ? 'drive.emptySearch' : EMPTY[view].title)}
            hint={q ? '' : t(EMPTY[view].hint)}
          />
        ) : (
          <DriveTable
            items={list.data.items}
            columns={q ? 'search' : view === 'trash' ? 'trash' : view === 'shared' ? 'shared' : 'mine'}
            onAction={actions.ask}
          />
        )}
      </Card>

      <UploadTray uploads={uploads} />
      {actions.dialog}
      {confirmEmpty && <EmptyBinDialog onDone={() => setConfirmEmpty(false)} />}
    </DropArea>
  );
}

const EMPTY: Record<ListView, { title: TranslationKey; hint: TranslationKey }> = {
  mine: { title: 'drive.emptyMine', hint: 'drive.emptyMineHint' },
  shared: { title: 'drive.emptyShared', hint: 'drive.emptySharedHint' },
  trash: { title: 'drive.emptyTrash', hint: 'drive.emptyTrashHint' },
};

/** A folder's page: where it is, what is in it, and — for whoever may — New folder and Upload. */
export function DriveFolderPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const actions = useDriveActions();
  const download = useFolderDownload();
  const folder = useQuery({
    queryKey: [...DRIVE_KEY, 'folder', id],
    queryFn: () => api.get<{ folder: DriveDetails; items: DriveItem[] }>(`/drive/folders/${id}`),
  });
  const data = folder.data;
  const head = data?.folder;
  const uploads = useDriveUploads(!!head?.can.add, head?.id ?? null, head?.name ?? '');

  if (folder.isLoading) return <LoadingBlock />;
  if (folder.error || !data || !head) {
    return <ErrorState error={folder.error ?? new Error(t('common.error'))} onRetry={() => void folder.refetch()} />;
  }

  return (
    <DropArea uploads={uploads}>
      <Breadcrumbs item={head} />
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Folder className="h-6 w-6 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
            <span className="break-all">{head.name}</span>
          </span>
        }
        subtitle={<ItemFacts item={head} />}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {head.can.add && <NewButtons parentId={head.id} onUpload={uploads.pick} />}
            {head.can.share && (
              <Button icon={<Share2 className="h-4 w-4" aria-hidden />} onClick={() => navigate(`/drive/${head.id}/share`)}>
                {t('drive.share')}
              </Button>
            )}
            {!!head.items && (
              <Button
                icon={<Download className="h-4 w-4" aria-hidden />}
                loading={download.busy}
                title={t('drive.downloadFolderHint')}
                onClick={() => download.start(head)}
              >
                {t('drive.download')}
              </Button>
            )}
            <RowMenu item={{ ...head, location: [] }} onAction={actions.ask} skipOpen />
          </div>
        }
      />
      <DriveTabs active={head.mine ? 'mine' : head.access === 'ADMIN' ? 'everyone' : 'shared'} />

      {head.binned && <BinNotice item={head} onRestore={() => actions.ask(head, 'restore')} />}

      <Card padded={false} className="mt-4">
        {!data.items.length ? (
          <EmptyState
            icon={<Folder className="h-8 w-8" aria-hidden />}
            title={t('drive.emptyFolder')}
            hint={t(head.can.add ? 'drive.emptyFolderHint' : 'drive.emptyFolderViewOnly')}
          />
        ) : (
          // An administrator's view of someone's folder: everything in it is that one owner's.
          <DriveTable items={data.items} columns={head.mine || head.access === 'ADMIN' ? 'mine' : 'shared'} onAction={actions.ask} />
        )}
      </Card>
      {head.can.add && (
        <p className="mt-3 text-caption text-faint">
          {t('drive.dropHint', { size: formatBytes((uploads.maxMb ?? 0) * 1024 * 1024, locale) })}
        </p>
      )}

      <UploadTray uploads={uploads} />
      {actions.dialog}
    </DropArea>
  );
}

/** A file's own page: shown where it can be — a picture, a video, a recording — its facts, and what may be done with it. */
export function DriveFilePage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const actions = useDriveActions();
  const item = useQuery({
    queryKey: [...DRIVE_KEY, 'item', id],
    queryFn: () => api.get<DriveDetails>(`/drive/items/${id}`),
  });
  const data = item.data;

  if (item.isLoading) return <LoadingBlock />;
  if (item.error || !data || !data.file) {
    return <ErrorState error={item.error ?? new Error(t('common.error'))} onRetry={() => void item.refetch()} />;
  }
  const file = data.file;
  const mime = file.mimeType;
  const Icon = iconFor(mime);
  const picture = /^image\/(png|jpeg|gif|webp)$/.test(mime);
  const video = /^video\/(mp4|x-m4v|quicktime|webm|ogg)$/.test(mime);
  const audio = mime.startsWith('audio/');
  const inTab = picture || mime === 'application/pdf' || video || audio;

  return (
    <>
      <Breadcrumbs item={data} />
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Icon className="h-6 w-6 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
            <span className="break-all">{data.name}</span>
          </span>
        }
        subtitle={<ItemFacts item={data} />}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <a href={file.downloadUrl} download>
              <Button variant="primary" icon={<Download className="h-4 w-4" aria-hidden />}>
                {t('drive.download')}
              </Button>
            </a>
            {inTab && (
              <a href={file.url} target="_blank" rel="noreferrer">
                <Button icon={<ExternalLink className="h-4 w-4" aria-hidden />}>{t('drive.openInTab')}</Button>
              </a>
            )}
            {data.can.share && (
              <Button icon={<Share2 className="h-4 w-4" aria-hidden />} onClick={() => navigate(`/drive/${data.id}/share`)}>
                {t('drive.share')}
              </Button>
            )}
            <RowMenu item={{ ...data, location: [] }} onAction={actions.ask} skipOpen />
          </div>
        }
      />

      {data.binned && <BinNotice item={data} onRestore={() => actions.ask(data, 'restore')} />}

      <div className="mt-4 grid items-start gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          {picture ? (
            <a href={file.url} target="_blank" rel="noreferrer" className="block">
              <img
                src={file.url}
                alt={data.name}
                className="mx-auto max-h-[70vh] max-w-full rounded-md object-contain"
              />
            </a>
          ) : video ? (
            <video src={file.url} controls preload="metadata" className="mx-auto max-h-[70vh] w-full rounded-md bg-black" />
          ) : audio ? (
            <audio src={file.url} controls preload="metadata" className="w-full" />
          ) : (
            <div className="flex flex-col items-center gap-3 py-14 text-center">
              <Icon className="h-12 w-12 text-[var(--text-tertiary)]" aria-hidden />
              <p className="text-body-sm text-muted max-w-sm">
                {t(mime === 'application/pdf' ? 'drive.pdfHint' : 'drive.downloadOnly')}
              </p>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader title={t('drive.details')} />
            <dl>
              <Detail label={t('drive.size')}>
                <span className="tabular">{formatBytes(file.sizeBytes, locale)}</span>
              </Detail>
              <Detail label={t('drive.owner')}>{localized(data.owner)}</Detail>
              {data.addedBy && data.addedBy.id !== data.owner.id && (
                <Detail label={t('drive.addedBy')}>{localized(data.addedBy)}</Detail>
              )}
              <Detail label={t('drive.created')}>{formatDateTime(data.createdAt, locale)}</Detail>
              <Detail label={t('drive.modified')}>{formatDateTime(data.updatedAt, locale)}</Detail>
              <Detail label={t('drive.about')}>
                <AboutChips item={data} empty={t('drive.aboutNothing')} />
              </Detail>
              <Detail label={t('drive.access')}>{t(`drive.access.${data.access}` as TranslationKey)}</Detail>
            </dl>
          </Card>
          {data.sharing && <SharingSummary item={data} />}
        </div>
      </div>

      {actions.dialog}
    </>
  );
}

/**
 * Everyone's drives — an administrator's (`drive.view_all`; operator, 2026-11-02: "admins can see it
 * and share it"): every employee who has anything in Drive, how much, and when it last changed. An
 * employee no longer active keeps theirs, to be opened and shared on.
 */
export function DriveEveryonePage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: [...DRIVE_KEY, 'drives'],
    queryFn: () => api.get<DriveOwnerSummary[]>('/drive/drives'),
  });

  return (
    <>
      <PageHeader title={t('drive.title')} subtitle={t('drive.everyoneHint')} />
      <DriveTabs active="everyone" />
      <Card padded={false} className="mt-4">
        {list.isLoading ? (
          <LoadingBlock />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.length ? (
          <EmptyState icon={<HardDrive className="h-8 w-8" aria-hidden />} title={t('drive.everyoneEmpty')} hint="" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('drive.col.employee')}</Th>
                <Th align="end" className="hidden sm:table-cell">
                  {t('drive.col.files')}
                </Th>
                <Th align="end">{t('drive.col.size')}</Th>
                <Th className="hidden md:table-cell">{t('drive.col.lastChange')}</Th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((d) => (
                <Tr key={d.owner.id} onClick={() => navigate(`/drive/everyone/${d.owner.id}`)}>
                  <Td>
                    <div className="flex items-center gap-2.5">
                      <HardDrive className="h-5 w-5 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                      <div className="min-w-0">
                        <Link
                          to={`/drive/everyone/${d.owner.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="block truncate font-medium hover:underline"
                        >
                          {localized(d.owner)}
                        </Link>
                        <span className="flex flex-wrap items-center gap-2 text-caption text-faint">
                          {d.owner.jobTitle}
                          {!d.owner.isActive && <Badge tone="warning">{t('drive.inactive')}</Badge>}
                        </span>
                      </div>
                    </div>
                  </Td>
                  <Td align="end" className="hidden sm:table-cell tabular text-muted">
                    {d.files}
                  </Td>
                  <Td align="end" className="tabular text-muted whitespace-nowrap">
                    {formatBytes(d.bytes, locale)}
                  </Td>
                  <Td className="hidden md:table-cell text-muted whitespace-nowrap">
                    <span title={formatDateTime(d.updatedAt, locale)}>{relativeTime(d.updatedAt, locale)}</span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

/** One employee's drive, as an administrator sees it: its top, to open and share from. */
export function DriveOwnerPage() {
  const { userId = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const actions = useDriveActions();
  const drive = useQuery({
    queryKey: [...DRIVE_KEY, 'drives', userId],
    queryFn: () =>
      api.get<{ owner: UserRef & { isActive: boolean }; items: DriveItem[]; usage: { files: number; bytes: number } }>(
        `/drive/drives/${userId}`,
      ),
  });
  const data = drive.data;

  if (drive.isLoading) return <LoadingBlock />;
  if (drive.error || !data) {
    return <ErrorState error={drive.error ?? new Error(t('common.error'))} onRetry={() => void drive.refetch()} />;
  }
  return (
    <>
      <nav aria-label={t('drive.where')} className="mb-3 flex flex-wrap items-center gap-1 text-body-sm text-muted">
        <Link to="/drive/everyone" className="hover:text-[var(--text-primary)] hover:underline">
          {t('drive.everyoneTab')}
        </Link>
      </nav>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {t('drive.driveOf', { name: localized(data.owner) })}
            {!data.owner.isActive && <Badge tone="warning">{t('drive.inactive')}</Badge>}
          </span>
        }
        subtitle={t('drive.usage', { files: data.usage.files, size: formatBytes(data.usage.bytes, locale) })}
      />
      <DriveTabs active="everyone" />
      <p className="mt-4 text-body-sm text-muted">{t('drive.ownerDriveHint')}</p>
      <Card padded={false} className="mt-3">
        {!data.items.length ? (
          <EmptyState icon={<HardDrive className="h-8 w-8" aria-hidden />} title={t('drive.ownerDriveEmpty')} hint="" />
        ) : (
          <DriveTable items={data.items} columns="mine" onAction={actions.ask} />
        )}
      </Card>
      {actions.dialog}
    </>
  );
}

/**
 * A client's or a project's Drive tab: every folder and file about it that the
 * reader may open — theirs, and what is shared with them — wherever it is.
 */
export function DriveRelated({ clientId, projectId }: { clientId?: string; projectId?: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const actions = useDriveActions();
  const params = clientId ? { clientId } : { projectId };
  const list = useQuery({
    queryKey: [...DRIVE_KEY, 'search', params],
    queryFn: () => api.get<{ items: DriveItem[] }>('/drive/search', params),
  });
  const query = clientId ? `clientId=${clientId}` : `projectId=${projectId}`;

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button icon={<FolderPlus className="h-4 w-4" aria-hidden />} onClick={() => navigate(`/drive/new-folder?${query}`)}>
          {t('drive.newFolderFor')}
        </Button>
      </div>
      <Card padded={false}>
        {list.isLoading ? (
          <LoadingBlock />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data?.items.length ? (
          <EmptyState
            icon={<HardDrive className="h-8 w-8" aria-hidden />}
            title={t(clientId ? 'drive.relatedEmptyClient' : 'drive.relatedEmptyProject')}
            hint={t('drive.relatedHint')}
          />
        ) : (
          <DriveTable items={list.data.items} columns="search" onAction={actions.ask} />
        )}
      </Card>
      {actions.dialog}
    </>
  );
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

type Columns = 'mine' | 'shared' | 'trash' | 'search';

function DriveTable({
  items,
  columns,
  onAction,
}: {
  items: DriveItem[];
  columns: Columns;
  onAction: (item: DriveItem | DriveDetails, action: DriveAction) => void;
}) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const showOwner = columns !== 'mine' && columns !== 'trash';
  const showWhere = columns === 'search' || columns === 'trash';

  return (
    <Table>
      <thead>
        <tr>
          <Th>{t('drive.col.name')}</Th>
          {showOwner && <Th className="hidden md:table-cell">{t('drive.col.owner')}</Th>}
          {showWhere && <Th className="hidden lg:table-cell">{t('drive.col.where')}</Th>}
          <Th className="hidden lg:table-cell">{t('drive.col.about')}</Th>
          <Th className="hidden sm:table-cell">{t(columns === 'trash' ? 'drive.col.trashed' : 'drive.col.modified')}</Th>
          <Th align="end" className="hidden sm:table-cell">
            {t('drive.col.size')}
          </Th>
          <Th align="end">
            <span className="sr-only">{t('drive.menu')}</span>
          </Th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const Icon = item.kind === 'FOLDER' ? Folder : iconFor(item.file?.mimeType ?? '');
          const open = columns === 'trash' && item.kind === 'FILE' ? undefined : () => navigate(drivePage(item));
          return (
            <Tr key={item.id} onClick={open}>
              <Td className="max-w-[24rem]">
                <div className="flex items-center gap-2.5">
                  <Icon
                    className={clsx(
                      'h-5 w-5 shrink-0',
                      item.kind === 'FOLDER' ? 'text-warning-fg dark:text-warning-dark' : 'text-[var(--text-tertiary)]',
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    {open ? (
                      <Link
                        to={drivePage(item)}
                        onClick={(e) => e.stopPropagation()}
                        className="block truncate font-medium hover:underline"
                        title={item.name}
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <span className="block truncate font-medium" title={item.name}>
                        {item.name}
                      </span>
                    )}
                    <ShareMarks item={item} />
                  </div>
                </div>
              </Td>
              {showOwner && (
                <Td className="hidden md:table-cell text-muted">{item.mine ? t('drive.you') : localized(item.owner)}</Td>
              )}
              {showWhere && (
                <Td className="hidden lg:table-cell text-muted">
                  <span className="block max-w-[14rem] truncate" title={item.location.map((p) => p.name).join(' › ')}>
                    {item.location.length ? item.location.map((p) => p.name).join(' › ') : t(item.mine ? 'drive.myDrive' : 'drive.shared')}
                  </span>
                </Td>
              )}
              <Td className="hidden lg:table-cell">
                <AboutChips item={item} />
              </Td>
              <Td className="hidden sm:table-cell text-muted whitespace-nowrap" >
                <span title={formatDateTime(columns === 'trash' ? item.trashedAt : item.updatedAt, locale)}>
                  {relativeTime(columns === 'trash' ? item.trashedAt : item.updatedAt, locale)}
                </span>
              </Td>
              <Td align="end" className="hidden sm:table-cell text-muted whitespace-nowrap tabular">
                {item.kind === 'FOLDER' ? t('drive.items', { n: item.items ?? 0 }) : formatBytes(item.file?.sizeBytes, locale)}
              </Td>
              <Td align="end" className="w-12">
                <RowMenu item={item} onAction={onAction} />
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </Table>
  );
}

/** Its owner's marks for what an item is shared with — people, departments, everyone, a public link, client contacts. */
function ShareMarks({ item }: { item: DriveItem | DriveDetails }) {
  const { t } = useI18n();
  const s = item.shared;
  if (!s) {
    return item.access === 'OWNER' ? null : (
      <span className="text-caption text-faint">{t(`drive.access.${item.access}` as TranslationKey)}</span>
    );
  }
  const marks: Array<{ icon: ReactNode; label: string }> = [];
  if (s.people) marks.push({ icon: <Users className="h-3 w-3" aria-hidden />, label: t('drive.sharedPeople', { n: s.people }) });
  if (s.departments) {
    marks.push({ icon: <Building className="h-3 w-3" aria-hidden />, label: t('drive.sharedDepartments', { n: s.departments }) });
  }
  if (s.everyone) marks.push({ icon: <Building2 className="h-3 w-3" aria-hidden />, label: t('drive.sharedEveryone') });
  if (s.publicLink) marks.push({ icon: <Globe className="h-3 w-3" aria-hidden />, label: t('drive.publicLinkOn') });
  if (s.contacts) marks.push({ icon: <Mail className="h-3 w-3" aria-hidden />, label: t('drive.contactsOn', { n: s.contacts }) });
  if (!marks.length) return null;
  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-caption text-faint">
      {marks.map((m) => (
        <span key={m.label} className="inline-flex items-center gap-1">
          {m.icon}
          {m.label}
        </span>
      ))}
    </span>
  );
}

/** What an item is about: its client, its project. */
function AboutChips({ item, empty }: { item: DriveItem | DriveDetails; empty?: string }) {
  const { localized } = useI18n();
  if (!item.client && !item.project) return empty ? <span className="text-muted">{empty}</span> : null;
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      {item.client && (
        <Badge tone="info" title={item.client.code}>
          {localized({ name: item.client.companyName, nameAr: item.client.companyNameAr })}
        </Badge>
      )}
      {item.project && (
        <Badge tone="neutral" title={item.project.code}>
          {localized(item.project)}
        </Badge>
      )}
    </span>
  );
}

/** Who owns it, who added it, when it changed — under an item's title. */
function ItemFacts({ item }: { item: DriveDetails }) {
  const { t, locale, localized } = useI18n();
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span>{item.mine ? t('drive.myItem') : t('drive.ownedBy', { name: localized(item.owner) })}</span>
      {!item.mine && <Badge tone="neutral">{t(`drive.access.${item.access}` as TranslationKey)}</Badge>}
      <span className="text-faint">{t('drive.changed', { when: relativeTime(item.updatedAt, locale) })}</span>
      <AboutChips item={item} />
    </span>
  );
}

/** My Drive (or Shared with me) › each folder above — as far up as the reader may see — and the item. */
function Breadcrumbs({ item }: { item: DriveDetails }) {
  const { t, localized } = useI18n();
  // An administrator walks someone's drive from its top; whoever it is shared with, from what was shared.
  const root = item.mine
    ? { to: '/drive', label: t('drive.myDrive') }
    : item.access === 'ADMIN'
      ? { to: `/drive/everyone/${item.owner.id}`, label: t('drive.driveOf', { name: localized(item.owner) }) }
      : { to: '/drive/shared', label: t('drive.shared') };
  return (
    <nav aria-label={t('drive.where')} className="mb-3 flex flex-wrap items-center gap-1 text-body-sm text-muted">
      <Link to={root.to} className="hover:text-[var(--text-primary)] hover:underline">
        {root.label}
      </Link>
      {item.path.map((p) => (
        <span key={p.id} className="inline-flex items-center gap-1">
          <ChevronRight className="h-3.5 w-3.5 flip-rtl text-faint" aria-hidden />
          <Link to={`/drive/folders/${p.id}`} className="max-w-[12rem] truncate hover:text-[var(--text-primary)] hover:underline">
            {p.name}
          </Link>
        </span>
      ))}
    </nav>
  );
}

function DriveTabs({ active }: { active: DriveView }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  return (
    <Tabs
      active={active}
      onChange={(id) => navigate(VIEWS.find((v) => v.id === id)!.to)}
      tabs={VIEWS.filter((v) => !v.permission || can(v.permission)).map((v) => ({ id: v.id, label: t(v.label) }))}
    />
  );
}

/** Search everything one may open, by name. */
function SearchBox({ initial }: { initial: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState(initial);
  useEffect(() => setQ(initial), [initial]);
  return (
    <form
      role="search"
      className="relative mt-4 max-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        navigate(q.trim() ? `/drive?q=${encodeURIComponent(q.trim())}` : '/drive');
      }}
    >
      <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" aria-hidden />
      <Input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('drive.searchPlaceholder')}
        aria-label={t('drive.searchPlaceholder')}
        className="ps-9"
      />
    </form>
  );
}

function NewButtons({ parentId, onUpload }: { parentId: string | null; onUpload: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <>
      <Button
        icon={<FolderPlus className="h-4 w-4" aria-hidden />}
        onClick={() => navigate(parentId ? `/drive/new-folder?parent=${parentId}` : '/drive/new-folder')}
      >
        {t('drive.newFolder')}
      </Button>
      <Button variant="primary" icon={<Upload className="h-4 w-4" aria-hidden />} onClick={onUpload}>
        {t('drive.upload')}
      </Button>
    </>
  );
}

function BinNotice({ item, onRestore }: { item: DriveDetails; onRestore: () => void }) {
  const { t } = useI18n();
  return (
    <Card className="mt-4 border-warning/40">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-body-sm text-warning-fg dark:text-warning-dark">
          {t(item.can.restore ? 'drive.inTrashNotice' : 'drive.inTrashFolderNotice')}
        </p>
        {item.can.restore && (
          <Button size="sm" icon={<RotateCcw className="h-4 w-4" aria-hidden />} onClick={onRestore}>
            {t('drive.restore')}
          </Button>
        )}
      </div>
    </Card>
  );
}

/** A file's sharing at a glance, for whoever shares it — its owner, its editors, an administrator — with the way to change it. */
function SharingSummary({ item }: { item: DriveDetails }) {
  const { t, localized } = useI18n();
  const navigate = useNavigate();
  const s = item.sharing!;
  const nobody =
    !s.people.length && !s.departments.length && !s.everyoneRole && !s.publicLink?.live && !s.contactLinks.some((l) => l.live);
  return (
    <Card>
      <CardHeader title={t('drive.sharing')} />
      {nobody && !s.through.length ? (
        <p className="text-body-sm text-muted">{t('drive.notShared')}</p>
      ) : (
        <ul className="space-y-1.5 text-body-sm">
          {s.people.map((p) => (
            <li key={p.user.id} className="flex items-center justify-between gap-3">
              <span className="truncate">{localized(p.user)}</span>
              <span className="text-caption text-muted">{t(p.role === 'EDIT' ? 'drive.roleEdit' : 'drive.roleView')}</span>
            </li>
          ))}
          {s.departments.map((d) => (
            <li key={d.department.id} className="flex items-center justify-between gap-3">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <Building className="h-3.5 w-3.5 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                <span className="truncate">{localized(d.department)}</span>
              </span>
              <span className="text-caption text-muted">{t(d.role === 'EDIT' ? 'drive.roleEdit' : 'drive.roleView')}</span>
            </li>
          ))}
          {s.everyoneRole && (
            <li className="flex items-center justify-between gap-3">
              <span>{t('drive.everyone')}</span>
              <span className="text-caption text-muted">{t(s.everyoneRole === 'EDIT' ? 'drive.roleEdit' : 'drive.roleView')}</span>
            </li>
          )}
          {s.publicLink?.live && <li>{t('drive.publicLinkOn')}</li>}
          {s.contactLinks.some((l) => l.live) && (
            <li>{t('drive.contactsOn', { n: s.contactLinks.filter((l) => l.live).length })}</li>
          )}
          {s.through.map((f) => (
            <li key={f.id} className="text-caption text-muted">
              {t('drive.through', { folder: f.name })}
            </li>
          ))}
        </ul>
      )}
      {item.can.share && (
        <Button size="sm" className="mt-4 w-full" onClick={() => navigate(`/drive/${item.id}/share`)}>
          {t('drive.manageSharing')}
        </Button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// What can be done to an item: its menu, and the questions it asks first
// ---------------------------------------------------------------------------

type DriveAction = 'trash' | 'restore' | 'purge';

/**
 * A row's menu — Open, Download, Share, Rename and details, Move, and the Trash —
 * offered as far as the reader's way in allows. It is drawn over the page (in a
 * portal), as a table that scrolls sideways would cut it off.
 */
function RowMenu({
  item,
  onAction,
  skipOpen = false,
}: {
  item: DriveItem | DriveDetails;
  onAction: (item: DriveItem | DriveDetails, action: DriveAction) => void;
  /** On the item's own page: Open would go nowhere. */
  skipOpen?: boolean;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const download = useFolderDownload();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; left: number; up: boolean } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const binned = item.trashedAt !== null || ('binned' in item && item.binned);
  const owner = item.access === 'OWNER';
  type Entry = { key: string; label: string; icon: ReactNode; run: () => void; danger?: boolean };
  const entries: Entry[] = [];
  if (!skipOpen && !(binned && item.kind === 'FILE')) {
    entries.push({ key: 'open', label: t('drive.open'), icon: <FolderInput className="h-4 w-4" />, run: () => navigate(drivePage(item)) });
  }
  if (item.file) {
    entries.push({
      key: 'download',
      label: t('drive.download'),
      icon: <Download className="h-4 w-4" />,
      run: () => save(item.file!.downloadUrl, item.name),
    });
  } else if (item.items) {
    // A folder, with everything in it, as one .zip.
    entries.push({ key: 'download', label: t('drive.downloadZip'), icon: <Download className="h-4 w-4" />, run: () => download.start(item) });
  }
  if (!binned) {
    // Sharing is its owner's, its editors' and an administrator's; changing it, its owner's and the editors'.
    if (item.can.share) entries.push({ key: 'share', label: t('drive.share'), icon: <Share2 className="h-4 w-4" />, run: () => navigate(`/drive/${item.id}/share`) });
    if (item.can.edit) {
      entries.push({ key: 'edit', label: t('drive.edit'), icon: <Pencil className="h-4 w-4" />, run: () => navigate(`/drive/${item.id}/edit`) });
      entries.push({ key: 'move', label: t('drive.move'), icon: <FolderInput className="h-4 w-4" />, run: () => navigate(`/drive/${item.id}/move`) });
      entries.push({ key: 'trash', label: t('drive.toTrash'), icon: <Trash2 className="h-4 w-4" />, run: () => onAction(item, 'trash'), danger: true });
    }
  } else if (owner && item.trashedAt) {
    entries.push({ key: 'restore', label: t('drive.restore'), icon: <RotateCcw className="h-4 w-4" />, run: () => onAction(item, 'restore') });
    entries.push({ key: 'purge', label: t('drive.purge'), icon: <Trash2 className="h-4 w-4" />, run: () => onAction(item, 'purge'), danger: true });
  }

  const close = useCallback((focus = false) => {
    setOpen(false);
    if (focus) button.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !button.current || !menu.current) return;
    const rect = button.current.getBoundingClientRect();
    const rtl = getComputedStyle(button.current).direction === 'rtl';
    const width = menu.current.offsetWidth;
    const height = menu.current.offsetHeight;
    const up = rect.bottom + height + 12 > window.innerHeight && rect.top - height - 12 > 0;
    // Its end edge under the button's: right in English, left in Arabic.
    const left = rtl ? Math.min(window.innerWidth - width - 8, rect.left) : Math.max(8, rect.right - width);
    setAt({ top: up ? rect.top - height - 4 : rect.bottom + 4, left, up });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close(true);
    const onMove = () => close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [open, close]);

  if (!entries.length) return null;
  return (
    <>
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('drive.menu')}
        title={t('drive.menu')}
        onClick={(e) => {
          e.stopPropagation();
          setAt(null);
          setOpen(!open);
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={menu}
            role="menu"
            onClick={(e) => e.stopPropagation()}
            style={at ? { top: at.top, left: at.left } : { top: -9999, left: -9999 }}
            className="fixed z-dropdown min-w-[12rem] rounded-md surface-raised py-1 shadow-lg animate-boot-in"
          >
            {entries.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  entry.run();
                }}
                className={clsx(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-start text-body-sm hover:bg-[var(--surface-sunken)]',
                  entry.danger ? 'text-error-fg dark:text-error-dark' : 'text-[var(--text-primary)]',
                )}
              >
                <span aria-hidden>{entry.icon}</span>
                {entry.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

/** The Trash's three questions, asked before anything is done, and what follows each. */
function useDriveActions() {
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [asked, setAsked] = useState<{ item: DriveItem | DriveDetails; action: DriveAction } | null>(null);

  const run = useMutation({
    mutationFn: ({ item, action }: { item: DriveItem | DriveDetails; action: DriveAction }) =>
      action === 'trash'
        ? requestWithMessage(`/drive/items/${item.id}/trash`, { method: 'POST' })
        : action === 'restore'
          ? requestWithMessage(`/drive/items/${item.id}/restore`, { method: 'POST' })
          : requestWithMessage(`/drive/items/${item.id}`, { method: 'DELETE' }),
    onSuccess: ({ data }, { item, action }) => {
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      toast.success(
        action === 'trash'
          ? t('drive.trashed')
          : action === 'purge'
            ? t('drive.purged')
            : (data as { restoredTo?: string } | undefined)?.restoredTo === 'TOP'
              ? t('drive.restoredToTop')
              : t('drive.restored'),
      );
      setAsked(null);
      // Off the item's own page once it is gone from where it was.
      if (action === 'purge' && window.location.pathname.includes(item.id)) navigate('/drive/trash');
      if (action === 'trash' && window.location.pathname.includes(item.id)) {
        navigate(item.parentId ? `/drive/folders/${item.parentId}` : item.mine ? '/drive' : '/drive/shared');
      }
    },
    onError: (e: Error) => {
      toast.error(e instanceof ApiError ? e.message : t('common.error'));
      setAsked(null);
    },
  });

  const ask = useCallback((item: DriveItem | DriveDetails, action: DriveAction) => setAsked({ item, action }), []);

  let dialog: ReactNode = null;
  if (asked) {
    const { item, action } = asked;
    const folder = item.kind === 'FOLDER';
    const body =
      action === 'trash'
        ? item.mine
          ? t(folder ? 'drive.confirmTrashFolder' : 'drive.confirmTrashFile')
          : t('drive.confirmTrashShared', { owner: localized(item.owner) })
        : action === 'purge'
          ? t('drive.confirmPurgeBody')
          : t('drive.confirmRestoreBody');
    dialog = (
      <ConfirmDialog
        title={t(
          action === 'trash' ? 'drive.confirmTrashTitle' : action === 'purge' ? 'drive.confirmPurgeTitle' : 'drive.confirmRestoreTitle',
          { name: item.name },
        )}
        confirmLabel={t(action === 'trash' ? 'drive.toTrash' : action === 'purge' ? 'drive.purge' : 'drive.restore')}
        danger={action !== 'restore'}
        busy={run.isPending}
        onConfirm={() => run.mutate(asked)}
        onCancel={() => setAsked(null)}
      >
        <p>{body}</p>
      </ConfirmDialog>
    );
  }
  return { ask, dialog };
}

/**
 * A folder downloaded as one .zip, with everything in it (operator, 2026-11-03: "in drive add option to
 * download folder"): the API signs a link for it, and the browser saves what the link sends — straight
 * into its downloads, however large. `busy` while the link is being made.
 */
function useFolderDownload() {
  const { t } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const start = useCallback(
    (folder: { id: string }) => {
      setBusy(true);
      api
        .post<DriveDownload>(`/drive/items/${folder.id}/download`)
        .then((link) => save(link.url, link.name))
        .catch((e: Error) => {
          const reason =
            e instanceof ApiError && e.code === 'DRIVE_DOWNLOAD_REFUSED' ? (e.details as { reason?: string } | undefined)?.reason : undefined;
          toast.error(reason ? t(`drive.downloadRefused.${reason}` as TranslationKey) : e.message);
        })
        .finally(() => setBusy(false));
    },
    [toast, t],
  );
  return { start, busy };
}

/** Has the browser save what a link sends, under `name`, without leaving the page. */
function save(url: string, name: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function EmptyBinDialog({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const empty = useMutation({
    mutationFn: () => api.post('/drive/trash/empty'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
      toast.success(t('drive.emptied'));
      onDone();
    },
    onError: (e: Error) => {
      toast.error(e.message);
      onDone();
    },
  });
  return (
    <ConfirmDialog
      title={t('drive.confirmEmptyTitle')}
      confirmLabel={t('drive.emptyTrashAction')}
      danger
      busy={empty.isPending}
      onConfirm={() => empty.mutate()}
      onCancel={onDone}
    >
      <p>{t('drive.confirmEmptyBody')}</p>
    </ConfirmDialog>
  );
}

// ---------------------------------------------------------------------------
// Uploading: picked, or dropped on the page
// ---------------------------------------------------------------------------

interface DriveUpload {
  key: string;
  name: string;
  size: number;
  progress: number;
  status: 'waiting' | 'uploading' | 'done' | 'error';
  error?: string;
}

/** At most this many go up at once; the rest wait their turn. */
const AT_ONCE = 3;

/**
 * Files put in a folder (null: the top of one's own drive), one upload each
 * straight to the file storage, a few at a time. Nothing to attach afterwards:
 * each becomes its item when its upload finishes. Not `enabled`: this page
 * takes no uploads.
 */
function useDriveUploads(enabled: boolean, parentId: string | null, name: string) {
  const { t, locale } = useI18n();
  const target = useMemo(() => (enabled ? { parentId, name } : null), [enabled, parentId, name]);
  const queryClient = useQueryClient();
  const policy = useFilePolicy();
  const [items, setItems] = useState<DriveUpload[]>([]);
  const [dragging, setDragging] = useState(false);
  const queue = useRef<Array<{ key: string; file: File; parentId: string | null }>>([]);
  const running = useRef(0);
  const depth = useRef(0);
  const input = useRef<HTMLInputElement | null>(null);
  const maxMb = policy?.maxDriveFileMb ?? policy?.maxUploadMb;

  const patch = (key: string, change: Partial<DriveUpload>) =>
    setItems((list) => list.map((u) => (u.key === key ? { ...u, ...change } : u)));

  const failure = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiError) {
        if (e.code === 'FILE_TOO_LARGE') return t('drive.fileTooLarge', { mb: String((e.details as { maxMb?: number })?.maxMb ?? maxMb ?? '') });
        if (e.code === 'FILE_STORAGE_UNREACHABLE' || e.code === 'FILE_STORAGE_UNAVAILABLE') return t('drive.storageUnreachable');
        return e.message;
      }
      return t('drive.uploadFailed');
    },
    [t, maxMb],
  );

  const pump = useCallback(() => {
    while (running.current < AT_ONCE && queue.current.length) {
      const next = queue.current.shift()!;
      running.current++;
      patch(next.key, { status: 'uploading' });
      uploadToDrive<DriveItem>(next.file, next.file.name, next.parentId, {
        onProgress: (fraction) => patch(next.key, { progress: fraction }),
      })
        .then(() => {
          patch(next.key, { status: 'done', progress: 1 });
          void queryClient.invalidateQueries({ queryKey: DRIVE_KEY });
        })
        .catch((e) => patch(next.key, { status: 'error', error: failure(e) }))
        .finally(() => {
          running.current--;
          pump();
        });
    }
  }, [queryClient, failure]);

  const add = useCallback(
    (files: File[]) => {
      if (!target || !files.length) return;
      const tooBig = maxMb ? files.filter((f) => f.size > maxMb * 1024 * 1024) : [];
      const fresh = files.map((file) => ({
        key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        parentId: target.parentId,
      }));
      setItems((list) => [
        ...list.filter((u) => u.status !== 'done'),
        ...fresh.map(({ key, file }) => ({
          key,
          name: file.name,
          size: file.size,
          progress: 0,
          status: tooBig.includes(file) ? ('error' as const) : ('waiting' as const),
          error: tooBig.includes(file) ? t('drive.fileTooLarge', { mb: String(maxMb) }) : undefined,
        })),
      ]);
      queue.current.push(...fresh.filter(({ file }) => !tooBig.includes(file)));
      pump();
    },
    [target, maxMb, pump, t],
  );

  // One file input for the page, opened by Upload files.
  useEffect(() => {
    const el = document.createElement('input');
    el.type = 'file';
    el.multiple = true;
    el.onchange = () => {
      add(Array.from(el.files ?? []));
      el.value = '';
    };
    input.current = el;
    return () => {
      input.current = null;
    };
  }, [add]);

  const drag = {
    onDragEnter: (e: DragEvent) => {
      if (!target || !Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
      depth.current++;
      setDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (target && Array.from(e.dataTransfer.types).includes('Files')) e.preventDefault();
    },
    onDragLeave: () => {
      if (!target) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!target) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      add(Array.from(e.dataTransfer.files));
    },
  };

  return {
    items,
    dragging,
    target,
    maxMb,
    drag,
    pick: () => input.current?.click(),
    dismiss: () => setItems((list) => list.filter((u) => u.status === 'waiting' || u.status === 'uploading')),
    sizeLabel: (bytes: number) => formatBytes(bytes, locale),
  };
}

type Uploads = ReturnType<typeof useDriveUploads>;

/** The page, which takes files dropped on it — with a veil saying where they go. */
function DropArea({ uploads, children }: { uploads: Uploads; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <div {...uploads.drag} className="relative min-h-full">
      {children}
      {uploads.dragging && uploads.target && (
        <div className="pointer-events-none fixed inset-0 z-overlay grid place-items-center bg-primary/10 backdrop-blur-[1px]">
          <div className="rounded-lg border-2 border-dashed border-primary bg-[var(--surface-raised)] px-8 py-6 text-center shadow-lg">
            <Upload className="mx-auto mb-2 h-8 w-8 text-primary" aria-hidden />
            <p className="text-title text-[var(--text-primary)]">{t('drive.dropHere', { folder: uploads.target.name })}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/** What is going up, and what went wrong — at the foot of the screen until put away. */
function UploadTray({ uploads }: { uploads: Uploads }) {
  const { t } = useI18n();
  if (!uploads.items.length) return null;
  const busy = uploads.items.filter((u) => u.status === 'waiting' || u.status === 'uploading').length;
  return (
    <div className="fixed bottom-4 end-4 z-sticky w-[min(22rem,calc(100vw-2rem))] rounded-lg surface-raised shadow-lg">
      <div className="flex items-center justify-between gap-2 border-b hairline px-4 py-2.5">
        <span className="text-body-sm font-medium text-[var(--text-primary)]">
          {busy ? t('drive.uploading', { n: busy }) : t('drive.uploadsDone')}
        </span>
        {!busy && (
          <button
            type="button"
            onClick={uploads.dismiss}
            aria-label={t('drive.dismiss')}
            className="rounded-sm p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      <ul className="max-h-64 space-y-2.5 overflow-y-auto px-4 py-3">
        {uploads.items.map((u) => (
          <li key={u.key} className="text-caption">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[var(--text-primary)]" title={u.name}>
                {u.name}
              </span>
              <span
                className={clsx(
                  'shrink-0',
                  u.status === 'error' ? 'text-error-fg dark:text-error-dark' : u.status === 'done' ? 'text-success-fg dark:text-success' : 'text-muted',
                )}
              >
                {u.status === 'done'
                  ? t('drive.uploaded')
                  : u.status === 'error'
                    ? t('drive.uploadFailed')
                    : u.status === 'waiting'
                      ? t('drive.waiting')
                      : `${Math.round(u.progress * 100)}%`}
              </span>
            </div>
            {u.status === 'uploading' && (
              <div className="mt-1">
                <ProgressBar percent={u.progress * 100} />
              </div>
            )}
            {u.error && <p className="mt-0.5 text-error-fg dark:text-error-dark">{u.error}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}
